import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilePdfOutlined } from '@ant-design/icons';
import { Button, Col, Menu, Row, Spin, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useAuthContext } from '../../contexts/AuthContext';
import { useErrorMessage } from '../../utils/errorMessage';
import { generatePDF } from '../../utils/generatePDF';
import { LoadingModal } from '../LoadingModal/LoadingModal';
import { downloadTemplateDocuments } from '../../routes/mail-sender/utils/downloadTemplateDocuments';

const { Text } = Typography;

/**
 * Button component to generate and download a PDF document based on a template.
 *
 * @component
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.parameters - The collections name used for generating the document.
 * @param {string} props.use_case - The use case for the document generation.
 * @param {string} props.button_text - The text displayed on the button.
 * @param {function} [props.onClick] - Optional click handler for the button.
 * @returns {JSX.Element} The rendered button component.
 */
export const TemplateDocumentButton = ({
  parameters,
  use_case,
  template_type,
  button_text,
  icon,
  onClick,
  extraQuery,
  withIcon,
  downloadName,
  buttonType,
  fromMenu,
  menuKey,
  downloadItems,
  type,
  id,
  setLoading
}) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const { message } = useErrorMessage();
  const { dispatchAPI } = useAuthContext();

  const handleClick = () => {
    if (downloadItems && downloadItems.length > 0) {
      downloadTemplateDocuments({
        dispatchAPI,
        message,
        t,
        setLoading,
        type,
        id,
        items: downloadItems
      });
    } else {
      generatePDF({
        message,
        dispatchAPI,
        parameters: Array.isArray(parameters) ? parameters : [parameters],
        use_case,
        template_type,
        setIsLoading,
        extraQuery,
        downloadName
      });
    }
    if (onClick) onClick();
  };

  return fromMenu ? (
    <>
      <Menu.Item key={menuKey} onClick={handleClick}>
        <Row gutter={10}>
          <Col>{icon}</Col>
          <Col>
            <Text>{t(button_text)}</Text>
          </Col>
        </Row>
      </Menu.Item>
      <LoadingModal
        openModal={isLoading}
        loadingText="template-documents.form.document_being_generated"
      />
    </>
  ) : (
    <Button
      type={icon ? 'link' : buttonType}
      onClick={handleClick}
      icon={isLoading ? <Spin /> : icon || <FilePdfOutlined />}
      loading={isLoading}
    >
      {(!icon || withIcon) && `${t(button_text)} `}
    </Button>
  );
};

TemplateDocumentButton.propTypes = {
  parameters: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        collection: PropTypes.string,
        documentId: PropTypes.string
      })
    ),
    PropTypes.shape({
      collection: PropTypes.string,
      documentId: PropTypes.string
    })
  ]).isRequired,
  use_case: PropTypes.string.isRequired,
  template_type: PropTypes.string,
  button_text: PropTypes.string,
  icon: PropTypes.node,
  onClick: PropTypes.func,
  extraQuery: PropTypes.string,
  withIcon: PropTypes.bool,
  downloadName: PropTypes.string,
  buttonType: PropTypes.string,
  fromMenu: PropTypes.bool,
  menuKey: PropTypes.string,
  downloadItems: PropTypes.arrayOf(PropTypes.shape({})),
  type: PropTypes.string,
  id: PropTypes.string,
  setLoading: PropTypes.func
};

TemplateDocumentButton.defaultProps = {
  icon: null,
  onClick: null,
  button_text: 'template-documents.form.generate_PDF',
  template_type: 'TEMPLATE_DOCS',
  extraQuery: '',
  withIcon: false,
  downloadName: 'document',
  buttonType: '',
  fromMenu: false,
  menuKey: null,
  downloadItems: null,
  type: null,
  id: null,
  setLoading: null
};
